/**
 * VentPlacer - Component for placing supply registers and return grilles
 */

import React, { useState } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { Select } from '../ui/Input';
import {
  VENT_TYPES,
  SUPPLY_REGISTER_SIZES,
  RETURN_GRILLE_SIZES,
} from '../../constants/hvacEquipment';

export default function VentPlacer({
  ventType,
  setVentType,
  ventSize,
  setVentSize,
  onClose,
  x = 20,
  y = 200,
  isMobile = false,
}) {
  const [minimized, setMinimized] = useState(isMobile);
  const currentVent = VENT_TYPES[ventType] || VENT_TYPES['supply-floor'];
  const sizes = currentVent.sizes || SUPPLY_REGISTER_SIZES;

  // Build vent type options grouped by category
  const supplyOptions = Object.entries(VENT_TYPES)
    .filter(([_, v]) => v.category === 'supply')
    .map(([key, v]) => ({ value: key, label: v.label }));

  const returnOptions = Object.entries(VENT_TYPES)
    .filter(([_, v]) => v.category === 'return')
    .map(([key, v]) => ({ value: key, label: v.label }));

  const sizeOptions = sizes.map((size) => ({
    value: size.value,
    label: size.label,
  }));

  return (
    <FloatingPanel
      title="Place Vent"
      onClose={onClose}
      onMinimize={() => setMinimized(!minimized)}
      minimized={minimized}
      isMobile={isMobile}
      x={x}
      y={y}
      maxHeight={350}
    >
      <div style={{ padding: '12px' }}>
        {/* Current Vent Info */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
            padding: '8px',
            background: `${currentVent.color}22`,
            borderRadius: '6px',
            border: `1px solid ${currentVent.color}44`,
          }}
        >
          <VentIcon type={ventType} color={currentVent.color} />
          <div>
            <div style={{ fontWeight: '600', color: '#fff' }}>
              {currentVent.label}
            </div>
            <div style={{ fontSize: '11px', color: '#8090a0' }}>
              {currentVent.category === 'supply' ? 'Supply Register' : 'Return Grille'}
            </div>
          </div>
        </div>

        <PanelSection title="Supply Registers">
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {supplyOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={ventType === opt.value ? 'primary' : 'secondary'}
                size="small"
                onClick={() => {
                  setVentType(opt.value);
                  const newVent = VENT_TYPES[opt.value];
                  setVentSize(newVent.defaultSize);
                }}
                style={{ fontSize: '11px', padding: '6px 8px' }}
              >
                {opt.label.replace(' Register', '')}
              </Button>
            ))}
          </div>
        </PanelSection>

        <PanelSection title="Return Grilles">
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {returnOptions.map((opt) => (
              <Button
                key={opt.value}
                variant={ventType === opt.value ? 'primary' : 'secondary'}
                size="small"
                onClick={() => {
                  setVentType(opt.value);
                  const newVent = VENT_TYPES[opt.value];
                  setVentSize(newVent.defaultSize);
                }}
                style={{ fontSize: '11px', padding: '6px 8px' }}
              >
                {opt.label.replace(' Grille', '').replace(' Return', '')}
              </Button>
            ))}
          </div>
        </PanelSection>

        <PanelSection title="Size">
          <Select
            label="Vent Size"
            value={ventSize}
            onChange={(size) => setVentSize(size)}
            options={sizeOptions}
          />
        </PanelSection>

        <div
          style={{
            marginTop: '12px',
            padding: '8px',
            background: 'rgba(0,200,255,0.1)',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#8090a0',
          }}
        >
          <strong style={{ color: '#00c8ff' }}>Tip:</strong> Click on the floor plan to place. Press <strong>R</strong> to rotate.
        </div>
      </div>
    </FloatingPanel>
  );
}

// SVG icon for vent types
function VentIcon({ type, color }) {
  const isSupply = type.startsWith('supply');

  return (
    <svg width="24" height="24" viewBox="0 0 32 32">
      {isSupply ? (
        <>
          <rect
            x="4"
            y="8"
            width="24"
            height="16"
            fill={color}
            fillOpacity="0.3"
            stroke={color}
            strokeWidth="1.5"
          />
          <line x1="6" y1="12" x2="26" y2="12" stroke={color} strokeWidth="1" />
          <line x1="6" y1="16" x2="26" y2="16" stroke={color} strokeWidth="1" />
          <line x1="6" y1="20" x2="26" y2="20" stroke={color} strokeWidth="1" />
        </>
      ) : (
        <>
          <rect
            x="4"
            y="4"
            width="24"
            height="24"
            fill={color}
            fillOpacity="0.3"
            stroke={color}
            strokeWidth="1.5"
          />
          <line x1="12" y1="4" x2="12" y2="28" stroke={color} strokeWidth="1" />
          <line x1="20" y1="4" x2="20" y2="28" stroke={color} strokeWidth="1" />
          <line x1="4" y1="12" x2="28" y2="12" stroke={color} strokeWidth="1" />
          <line x1="4" y1="20" x2="28" y2="20" stroke={color} strokeWidth="1" />
        </>
      )}
    </svg>
  );
}
